import { formatDayMonthYear } from '../../utils/dateFormat';

/** Words that read wrong once lower-cased: "Seo" → "SEO", "Linkedin" → "LinkedIn". */
const WORD_CASE: Record<string, string> = {
  seo: 'SEO',
  geo: 'GEO',
  crm: 'CRM',
  icp: 'ICP',
  utm: 'UTM',
  sdr: 'SDR',
  api: 'API',
  url: 'URL',
  ai: 'AI',
  g2: 'G2',
  dnc: 'DNC',
  mrr: 'MRR',
  arr: 'ARR',
  rfp: 'RFP',
  linkedin: 'LinkedIn',
  lemlist: 'lemlist',
  rb2b: 'RB2B',
};

/**
 * Turns a raw field name or slug into a readable label:
 * `buyerName` → "Buyer name", `contract_end_month` → "Contract end month",
 * `linkedin-outbound` → "LinkedIn outbound", `seo` → "SEO".
 */
export function humanize(raw: string | null | undefined): string {
  if (!raw) return '';
  const spaced = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const words = spaced.split(' ').map((w) => WORD_CASE[w] ?? w);
  const first = words[0] ?? '';
  if (first && !WORD_CASE[first.toLowerCase()]) words[0] = first.charAt(0).toUpperCase() + first.slice(1);
  return words.join(' ');
}

/** "today", "yesterday", "5d ago", then "7 Oct 2026" beyond a month. */
export function daysAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days)) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return formatDayMonthYear(iso);
}

/** ISO alpha-3 codes seen in tender data, mapped to alpha-2 so one display path names them. */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  AUT: 'AT', BEL: 'BE', BGR: 'BG', CHE: 'CH', CYP: 'CY', CZE: 'CZ', DEU: 'DE', DNK: 'DK', ESP: 'ES', EST: 'EE',
  FIN: 'FI', FRA: 'FR', GBR: 'GB', GRC: 'GR', HRV: 'HR', HUN: 'HU', IRL: 'IE', ISL: 'IS', ITA: 'IT', LIE: 'LI',
  LTU: 'LT', LUX: 'LU', LVA: 'LV', MLT: 'MT', NLD: 'NL', NOR: 'NO', POL: 'PL', PRT: 'PT', ROU: 'RO', SVK: 'SK',
  SVN: 'SI', SWE: 'SE', USA: 'US', CAN: 'CA', AUS: 'AU',
};

let regionNames: Intl.DisplayNames | null = null;
try {
  regionNames = new Intl.DisplayNames(['en-GB'], { type: 'region' });
} catch {
  regionNames = null;
}

/** "FRA" / "FR" / "France" → "France". Anything unrecognised is returned as given. */
export function countryName(raw: string | null | undefined): string {
  if (!raw) return '';
  const code = raw.trim().toUpperCase();
  const alpha2 = code.length === 3 ? ALPHA3_TO_ALPHA2[code] : code.length === 2 ? (code === 'UK' ? 'GB' : code) : null;
  if (alpha2 && regionNames) {
    try {
      const name = regionNames.of(alpha2);
      if (name && name !== alpha2) return name;
    } catch {
      // fall through to the raw value
    }
  }
  return raw;
}

/** Windows-1252 characters in 0x80–0x9F, so UTF-8 bytes read as cp1252 can be put back. */
const CP1252: Record<string, number> = {
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a,
  '‹': 0x8b, 'Œ': 0x8c, 'Ž': 0x8e, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97,
  '˜': 0x98, '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c, 'ž': 0x9e, 'Ÿ': 0x9f,
};

/**
 * Display-only repair for text that was UTF-8 decoded as Latin-1 upstream
 * ("SociÃ©tÃ©" → "Société", "â€™" → "’") and for stray U+FFFD replacement
 * characters. Leaves anything that does not look garbled untouched.
 */
export function cleanText(raw: string | null | undefined): string {
  if (!raw) return '';
  let text = raw;
  if (/[ÃÂâ][\u0080-¿€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]/.test(text)) {
    const bytes: number[] = [];
    let ok = true;
    for (const ch of text) {
      const code = ch.codePointAt(0)!;
      if (code < 0x100) bytes.push(code);
      else if (CP1252[ch] !== undefined) bytes.push(CP1252[ch]);
      else {
        ok = false;
        break;
      }
    }
    if (ok) {
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes));
      } catch {
        // not actually mojibake; keep the original
      }
    }
  }
  return text.replace(/�/g, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Display repair for tender text stored before the API repaired it at ingest
 * (mirrors `repairTedText` in packages/api/src/modules/tenders/clean.ts): TED's
 * own notices carry "?" where the publisher lost a character, and "EUR 1" /
 * "EUR 0" is a placeholder the buyer typed instead of the value. Rows written
 * from now on arrive clean; this only covers the older ones.
 */
export function repairTenderText(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/(^|[\s(«"'/-])(qu|Qu|QU|lorsqu|puisqu|jusqu|[dlnsjcmtDLNSJCMT])\?(?=\p{L})/gu, '$1$2’')
    .replace(/\?(uvre|UVRE)/g, (_m, rest: string) => (rest === 'UVRE' ? 'Œ' : 'œ') + rest)
    .replace(/\b([DdLl]['’])(UVRE|uvre)\b/g, (_m, pre: string, rest: string) => pre + (rest === 'UVRE' ? 'ŒUVRE' : 'œuvre'))
    .replace(/(\p{Lu})\?(\p{Lu})/gu, '$1-$2')
    .replace(/(\S) \? (?=\S)/g, '$1 – ')
    .replace(/\b(EUR|GBP|USD) (0|0\.\d+|1)(?![\d.,]*\d|[kM])/g, 'value not published');
}

/** Country names as TED titles spell them, for spotting a pasted country list. */
const COUNTRY_NAMES = new Set(
  [
    'Albania', 'Andorra', 'Armenia', 'Austria', 'Azerbaijan', 'Belarus', 'Belgium', 'Bosnia and Herzegovina',
    'Bulgaria', 'Croatia', 'Cyprus', 'Czechia', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France',
    'Georgia', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Israel', 'Italy', 'Kosovo', 'Latvia',
    'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Moldova', 'Monaco', 'Montenegro', 'Netherlands',
    'the Netherlands', 'North Macedonia', 'Norway', 'Poland', 'Portugal', 'Romania', 'San Marino', 'Serbia',
    'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland', 'Türkiye', 'Turkey', 'Ukraine', 'United Kingdom',
    'UK', 'United States', 'USA', 'Canada', 'Australia', 'Morocco', 'Tunisia', 'Egypt',
  ].map((n) => n.toLowerCase()),
);

/** A comma-separated run of five or more names. */
const NAME_RUN = /(?:[\p{Lu}][\p{L}'.-]*(?: (?:and )?[\p{L}'.-]+){0,2})(?:, (?:[\p{Lu}][\p{L}'.-]*(?: (?:and )?[\p{L}'.-]+){0,2})){4,}/gu;

/**
 * "Netherlands, Albania, Armenia, … United Kingdom – IT services" → "39 countries – IT services".
 * Only a run that is mostly country names collapses, so a list of suppliers is left alone.
 * `full` is the original text, for the hover.
 */
export function collapseCountryList(text: string): { text: string; full: string | null } {
  let changed = false;
  const out = text.replace(NAME_RUN, (run) => {
    const items = run.split(', ').map((s) => s.trim());
    const known = items.filter((i) => COUNTRY_NAMES.has(i.toLowerCase())).length;
    if (items.length < 5 || known / items.length < 0.8) return run;
    changed = true;
    return `${items.length} countries`;
  });
  return { text: out, full: changed ? text : null };
}

/**
 * Registry or CRM-export ids glued onto names inside running text:
 * "incumbent PFH Technology Group_17679, …" → "incumbent PFH Technology Group, …".
 * Only an underscore followed by digits at a word end is touched.
 */
export function stripRawIds(text: string): string {
  return text.replace(/\s?_\d{2,}(?:_\([^)]*\))?(?=$|[\s,;:)"'’.»])/g, '');
}

/**
 * The one money format across GTM: compact with at most one decimal, from a
 * thousand up, so €96.3k never sits beside €4,356 and Sourcing never shows
 * €698.63M. 4356 → "€4.4k", 96288 → "€96.3k", 698_630_000 → "€698.6M",
 * 950 → "€950". Empty for null/NaN/0 so callers keep their own fallback.
 * The full figure belongs in a tooltip (formatMoney).
 */
export function formatEuroCompact(value: number | null | undefined, currency = '€'): string {
  if (value == null || Number.isNaN(value) || value === 0) return '';
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  const one = (n: number) => String(Number(n.toFixed(1)));
  let body: string;
  if (abs >= 1_000_000_000) body = `${one(abs / 1_000_000_000)}B`;
  else if (abs >= 1_000_000) body = `${one(abs / 1_000_000)}M`;
  else if (abs >= 1_000) body = `${one(abs / 1_000)}k`;
  else body = String(Math.round(abs));
  // 999,950 rounds to "1000k": step up a unit instead.
  if (body === '1000k') body = '1M';
  if (body === '1000M') body = '1B';
  return `${sign}${currency}${body}`;
}

/**
 * The comparison word between two rates, judged at the one-decimal percentage
 * the page shows them at, so the sentence can never contradict its own
 * numbers: 6.4% vs 5.6% → "higher than", 5.0% vs 5.0% → "the same as".
 */
export function rateComparison(a: number, b: number): 'higher than' | 'lower than' | 'the same as' {
  const ra = Math.round(a * 1000);
  const rb = Math.round(b * 1000);
  if (ra > rb) return 'higher than';
  if (ra < rb) return 'lower than';
  return 'the same as';
}
